import i18n from 'app-i18n';
import { FileWithPath } from 'react-dropzone';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import * as yup from 'yup';
import { getEnv } from './getEnv';
import { DateTime } from 'luxon';
import { FILE_UPLOADING_STATUS } from 'components/forms/fields/file-upload-field/file-upload-field.utils';

export const localeMessages = {
  mixed: {
    default: props => i18n.t('forms.errors.mixed.default', props),
    required: props => i18n.t('forms.errors.mixed.required', props),
    defined: props => i18n.t('forms.errors.mixed.defined', props),
    oneOf: props => i18n.t('forms.errors.mixed.oneOf', props),
    notOneOf: props => i18n.t('forms.errors.mixed.notOneOf', props),
    notType: ({ path, type, value, originalValue }) => {
      /* istanbul ignore next */
      if (getEnv('NODE_ENV') === 'development') {
        const consoleMessage =
          type !== 'mixed'
            ? `${path} must be a \`${type}\` type, ` +
              `but the final value was: `
            : `${path} must match the configured type. ` +
              `The validated value was: `;

        console.warn(consoleMessage, value);

        if (originalValue != null && originalValue !== value)
          console.log('Cast from the value', originalValue);
      }

      return i18n.t('forms.errors.mixed.notType');
    },
  },
  string: {
    length: props => i18n.t('forms.errors.string.length', props),
    min: props => i18n.t('forms.errors.string.min', props),
    max: props => i18n.t('forms.errors.string.max', props),
    matches: props => i18n.t('forms.errors.string.matches', props),
    email: props => i18n.t('forms.errors.string.email', props),
    url: props => i18n.t('forms.errors.string.url', props),
    uuid: props => i18n.t('forms.errors.string.uuid', props),
    trim: props => i18n.t('forms.errors.string.trim', props),
    lowercase: props => i18n.t('forms.errors.string.lowercase', props),
    uppercase: props => i18n.t('forms.errors.string.uppercase', props),
  },
  number: {
    min: props => i18n.t('forms.errors.number.min', props),
    max: props => i18n.t('forms.errors.number.max', props),
    lessThan: props => i18n.t('forms.errors.number.lessThan', props),
    moreThan: props => i18n.t('forms.errors.number.moreThan', props),
    positive: props => i18n.t('forms.errors.number.positive', props),
    negative: props => i18n.t('forms.errors.number.negative', props),
    integer: props => i18n.t('forms.errors.number.integer', props),
  },
  date: {
    min: props => i18n.t('forms.errors.date.min', props),
    max: props => i18n.t('forms.errors.date.max', props),
  },
  object: {
    noUnknown: props => i18n.t('forms.errors.object.noUnknown', props),
  },
  array: {
    min: props => i18n.t('forms.errors.array.min', props),
    max: props => i18n.t('forms.errors.array.max', props),
    length: props => i18n.t('forms.errors.array.length', props),
  },
};

yup.setLocale(localeMessages);

yup.addMethod(yup.string, 'phoneNumber', function (errorMessage) {
  return this.test(`test-valid-phone`, errorMessage, function (value) {
    return !!value && isPossiblePhoneNumber(value)
      ? true
      : this.createError({
          message: i18n.t('forms.errors.string.phoneNumber'),
        });
  });
});

yup.addMethod(yup.mixed, 'flexDefault', function (defaultValue) {
  this.spec.default = defaultValue;
  return this.transform(value => {
    if (this.type === 'number')
      return value === null || isNaN(value) ? defaultValue : value;
    return value === null ? defaultValue : value;
  });
});

yup.addMethod(yup.array, 'unique', function (message, mapper = a => a) {
  return this.test('unique', message, function (list) {
    return list.length === new Set(list.map(mapper)).size
      ? true
      : this.createError({
          message: i18n.t('forms.errors.array.unique'),
        });
  });
});

const testFileType = (validFileTypes: string[], file: FileWithPath) => {
  const fileExt = file.name.split('.').reverse()[0];
  return validFileTypes.includes(fileExt.toLowerCase());
};

yup.addMethod(yup.array, 'fileTypes', function (fileTypes) {
  return this.test('fileTypes', function (list) {
    if (!list || list.length < 1) return true;

    const badFiles = list.reduce((result, file: FileWithPath) => {
      if (!testFileType(fileTypes, file)) {
        result.push(file.path ?? file.name);
      }
      return result;
    }, []);

    if (badFiles.length < 1) return true;
    else
      return this.createError({
        message: i18n.t('forms.errors.array.fileTypes', {
          fileTypes: fileTypes.join(', '),
          files: badFiles.join(', '),
        }),
      });
  });
});

const testFileSize = (maxSize: number, file: FileWithPath) => {
  return file.size < maxSize;
};

/**
 * maxSize in KB
 */
yup.addMethod(yup.array, 'fileSizes', function (maxSize) {
  return this.test('fileSizes', function (list) {
    if (!list || list.length < 1) return true;

    const badFiles = list.reduce((result, file: FileWithPath) => {
      if (!testFileSize(maxSize * 1000, file)) {
        result.push(file.path ?? file.name);
      }
      return result;
    }, []);

    if (badFiles.length < 1) return true;
    else
      return this.createError({
        message: i18n.t('forms.errors.array.fileSizes', {
          maxSize: maxSize / 1000,
          files: badFiles.join(', '),
        }),
      });
  });
});

yup.addMethod(
  yup.array,
  'hasAtLeastOneRuleForSelectedSectionTags',
  function () {
    return this.test(
      'has-at-least-one-rule-for-selected-section-tags',
      () => i18n.t('forms.errors.rules.atLeastOneRuleFromSection.customerTags'),
      function (values, context) {
        const {
          createError,
          path,
          parent: { ruleTypes },
        } = context;

        if (ruleTypes.includes('TAGS') && !values[0].childNodes[0]?.attribute)
          return createError({
            path: `${path}.0`,
            type: 'error',
          });

        return true;
      },
    );
  },
);

yup.addMethod(
  yup.array,
  'hasAtLeastOneRuleForSelectedSectionCustomTargetingTags',
  function () {
    return this.test(
      'has-at-least-one-rule-for-selected-section-custom-targeting-tags',
      () =>
        i18n.t(
          'forms.errors.rules.atLeastOneRuleFromSection.customTargetingTags',
        ),
      function (values, context) {
        const {
          createError,
          path,
          parent: { ruleTypes },
        } = context;

        if (
          ruleTypes.includes('TARGETING_RULES') &&
          !values[1].childNodes[0]?.childNodes[0]?.attribute
        )
          return createError({
            path: `${path}.1`,
            type: 'error',
          });

        return true;
      },
    );
  },
);

yup.addMethod(
  yup.array,
  'hasAtLeastOneRuleForSelectedSectionSegments',
  function () {
    return this.test(
      'has-at-least-one-rule-for-selected-section-segments',
      () => i18n.t('forms.errors.rules.atLeastOneRuleFromSection.segmentTags'),
      function (values, context) {
        const {
          createError,
          path,
          parent: { ruleTypes },
        } = context;

        if (
          ruleTypes.includes('SEGMENTS') &&
          !values[2].childNodes[0]?.attribute
        )
          return createError({
            path: `${path}.2`,
            type: 'error',
          });

        return true;
      },
    );
  },
);

yup.addMethod(
  yup.string,
  'validateDistributionEndDateWithDistributionStartDate',
  function () {
    return this.test({
      name: 'distribution-end-date-validations-with-distribution-start-date',
      test(value, context) {
        const {
          parent: { distributionStartDate },
          createError,
        } = context;
        if (value < distributionStartDate)
          return createError({
            message: i18n.t('forms.errors.distributionEndDate.min'),
          });
        return true;
      },
    });
  },
);

yup.addMethod(
  yup.string,
  'validateDistributionEndDateWithCampaignEndEndDate',
  function () {
    return this.test({
      name: 'distribution-end-date-validations-with-campaign-end-date',
      test(value, context) {
        const {
          parent: { campaignEndDate },
          createError,
        } = context;
        if (value > campaignEndDate)
          return createError({
            message: i18n.t(
              'forms.errors.distributionEndDate.campaignEndDate',
              { campaignEndDate },
            ),
          });
        return true;
      },
    });
  },
);

yup.addMethod(yup.string, 'distributionStartDateValidations', function () {
  return this.test({
    name: 'distribution-start-date-validations',
    test(value, context) {
      const {
        createError,
        parent: { campaignStartDate, campaignEndDate, timezone },
      } = context;

      if (value < DateTime.local({ zone: timezone }).toISODate())
        return createError({
          message: i18n.t('forms.errors.distributionStartDate.pastDate'),
        });

      if (value < campaignStartDate)
        return createError({
          message: i18n.t(
            'forms.errors.distributionStartDate.campaignStartDate',
            { campaignStartDate },
          ),
        });

      const effectiveValue =
        value ?? DateTime.local({ zone: timezone }).toISODate();
      if (effectiveValue > campaignEndDate)
        return createError({
          message: i18n.t(
            'forms.errors.distributionStartDate.campaignEndDate',
            { campaignEndDate },
          ),
        });
      return true;
    },
  });
});

const hasOtherRules = values =>
  values.some(
    val =>
      val.childNodes[0]?.attribute || val.childNodes[0]?.childNodes.length > 1,
  );

yup.addMethod(yup.array, 'noOtherRulesWithEveryone', function () {
  return this.test(
    `no-other-rules-with-everyone`,
    () => i18n.t('forms.errors.rules.otherRulesWithEveryone'),
    function (values) {
      const hasNoOneOrEveryoneRule = values[1].childNodes[0]?.childNodes.find(
        node => node.attribute?.id === 'EVERYONE',
      );
      if (hasNoOneOrEveryoneRule && hasOtherRules(values)) return false;
      return true;
    },
  );
});

yup.addMethod(yup.array, 'noOtherRulesWithNoOne', function () {
  return this.test(
    `no-other-rules-with-no-one`,
    () => i18n.t('forms.errors.rules.otherRulesWithNoOne'),
    function (values) {
      const hasNoOneOrEveryoneRule = values[1].childNodes[0]?.childNodes.find(
        node => node.attribute?.id === 'NO_ONE',
      );
      if (hasNoOneOrEveryoneRule && hasOtherRules(values)) return false;
      return true;
    },
  );
});

yup.addMethod(yup.string, 'validateWithTrailingCodeLength', function () {
  return this.test('max-value', (val, context) => {
    const trailingCodeLength = context.parent.trailingCodeLengthFromBasicStep;
    if (!!trailingCodeLength && Number(val) > Number(trailingCodeLength))
      return context.createError({
        message: i18n.t(
          'whenCanRedeem.distributionLimit.errors.maxDistributionCount',
          { trailingCodeLength },
        ),
      });
    return true;
  });
});

yup.addMethod(yup.string, 'mustBeAPositiveInteger', function () {
  return this.test(
    'must-be-a-number',
    () => i18n.t('forms.errors.string.positiveInteger'),
    rawVal => {
      const value = Number(rawVal);
      return !isNaN(value) && value > 0 && !rawVal.includes('.');
    },
  );
});

yup.addMethod(yup.string, 'singleImage', function () {
  return this.test(
    'not-uploading',
    () => i18n.t('FileUploadField.errors.notUploading'),
    value => value !== FILE_UPLOADING_STATUS,
  );
});

yup.addMethod(yup.array, 'multipleImages', function () {
  return this.test(
    'not-uploading',
    () => i18n.t('FileUploadField.errors.notUploading'),
    value =>
      !value.some(
        item =>
          typeof item === 'object' && item.status === FILE_UPLOADING_STATUS,
      ),
  );
});

/*
 * Four properties rather than one, because an emoji is not a single kind of
 * character, and three of these are built from characters that are not pictographic
 * at all — so each slipped past a pictographic-only check on its own:
 *
 *   - `Extended_Pictographic` is the bulk of them. ZWJ sequences come along for free,
 *     since they are pictographs joined by an invisible character.
 *   - `Regional_Indicator` is a flag, which is a pair of them and nothing else.
 *   - `Emoji_Modifier` is a skin tone. Attached to a hand it rides on the pictograph,
 *     but on its own it is only this property.
 *   - U+20E3 is the enclosing keycap, which follows an ordinary digit or `#`.
 *
 * Deliberately not `\p{Emoji}`, which matches bare `0-9`, `#` and `*` — it would
 * reject "Save 20%". Nor `\p{Emoji}` over a digit-stripped copy, which reads as the
 * self-updating version of this and is not: removing the `1` from a keycap leaves
 * U+FE0F and U+20E3, neither of which is `\p{Emoji}`, so keycaps come back.
 *
 * What is here admits accented Latin, CJK, Cyrillic and Arabic untouched. It does
 * refuse `©`, `®` and `™`, which are `Extended_Pictographic` and are
 * plausible in brand copy — known, and left alone here because it predates this rule
 * and is a product call rather than an engineering one.
 */
const EMOJI =
  /[\p{Extended_Pictographic}\p{Regional_Indicator}\p{Emoji_Modifier}\u20E3]/u;

yup.addMethod(yup.string, 'emojisAreNotSupported', function () {
  return this.test(
    'emojis-not-supported',
    () => i18n.t('forms.errors.string.emojis'),
    value => !EMOJI.test(value),
  );
});
